// 피드백 글 → '해당 화면에서 보기' 링크 (2026-10-06).
// 글에 붙은 요소 id 와 메모를 URL 파라미터(fbhl)에 실어 대상 화면을 연다. 대상 화면의 FeedbackHighlighter 가
// 파라미터를 읽어 요소를 박스로 표시한다. 피드백 조회 API(관리자 전용)를 대상 화면이 다시 부르지 않으므로
// 로그인 권한과 무관하게 동작한다.
import type { FeedbackDetail } from './types';

export const HIGHLIGHT_PARAM = 'fbhl';

export interface HighlightTarget {
  id: string;
  /** 이 요소에 대한 메모 (글에서 #id 뒤에 적은 내용) */
  note?: string;
}

export interface HighlightSpec {
  /** 피드백 글 번호 */
  fid?: number;
  /** 글 요약 (배너 표시) */
  title?: string;
  targets: HighlightTarget[];
}

const ID_RE = /#([A-Za-z][\w:-]*)/g;
const NOTE_MAX = 160;

/** 글(대화 로그 텍스트 · id pill · targetIds)에서 요소 id 와 id 별 메모를 뽑는다. 페이지 경로('/…')는 제외. */
export function extractTargets(detail: Pick<FeedbackDetail, 'targetIds' | 'chatLog'>): HighlightTarget[] {
  const order: string[] = [];
  const notes = new Map<string, string>();
  const add = (id: string, note?: string) => {
    if (!id || id.startsWith('/')) return;
    if (!order.includes(id)) order.push(id);
    const n = note?.trim();
    if (n && !notes.get(id)) notes.set(id, n.length > NOTE_MAX ? `${n.slice(0, NOTE_MAX)}…` : n);
  };

  let log: Array<{ kind?: string; content?: string }> = [];
  try {
    log = detail.chatLog ? JSON.parse(detail.chatLog) : [];
  } catch {
    log = [];
  }
  for (const m of log) {
    const text = m.content ?? '';
    if (m.kind === 'id') {
      add(text.replace(/^#/, ''));
      continue;
    }
    if (m.kind !== 'text') continue;
    // "#a 메모1 #b 메모2" → a: 메모1, b: 메모2. 메모는 다음 #id 또는 그 줄 끝까지 (글의 맺음말이 마지막 요소에 붙지 않게)
    const hits = Array.from(text.matchAll(ID_RE));
    hits.forEach((h, i) => {
      const start = (h.index ?? 0) + h[0].length;
      const nextId = i + 1 < hits.length ? hits[i + 1].index ?? text.length : text.length;
      const lineEnd = text.indexOf('\n', start);
      const end = lineEnd >= 0 ? Math.min(nextId, lineEnd) : nextId;
      add(h[1], text.slice(start, end).replace(/\s+/g, ' '));
    });
  }
  (detail.targetIds ?? '').split(',').map((s) => s.trim().replace(/^#/, '')).forEach((id) => add(id));
  return order.map((id) => ({ id, note: notes.get(id) }));
}

function toBase64Url(s: string): string {
  const bytes = new TextEncoder().encode(s);
  let bin = '';
  bytes.forEach((b) => { bin += String.fromCharCode(b); });
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function fromBase64Url(s: string): string {
  const b64 = s.replace(/-/g, '+').replace(/_/g, '/') + '==='.slice((s.length + 3) % 4);
  const bin = atob(b64);
  const bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

/** 피드백 글의 화면 주소에 강조 정보를 붙인 링크. 요소가 없으면 화면만 연다. */
export function buildHighlightUrl(detail: Pick<FeedbackDetail, 'id' | 'pageUrl' | 'summary' | 'targetIds' | 'chatLog'>): string | null {
  if (!detail.pageUrl) return null;
  let url: URL;
  try {
    url = new URL(detail.pageUrl, typeof window !== 'undefined' ? window.location.origin : undefined);
  } catch {
    return null;
  }
  const spec: HighlightSpec = {
    fid: detail.id,
    title: (detail.summary ?? '').slice(0, 120),
    targets: extractTargets(detail),
  };
  url.searchParams.set(HIGHLIGHT_PARAM, toBase64Url(JSON.stringify(spec)));
  return url.toString();
}

/** 현재 주소의 강조 정보 (없거나 형식 오류면 null). */
export function readHighlightSpec(search: string): HighlightSpec | null {
  const raw = new URLSearchParams(search).get(HIGHLIGHT_PARAM);
  if (!raw) return null;
  try {
    const spec = JSON.parse(fromBase64Url(raw)) as HighlightSpec;
    return Array.isArray(spec.targets) ? spec : null;
  } catch {
    return null;
  }
}
